import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { IMAGE_SCOPES, uploadContentImage } from "../../../../services/contentServices";
import ArticleImageCropDialog from "./ArticleImageCropDialog";
import { ARTICLE_HERO_IMAGE_ASPECT } from "./articleImageLayoutConstants";

import { contentEditorCardSx, contentCardContentSx, contentFieldSx, contentSectionTitleSx } from "../contentEditorStyles";

const ArticleMetadataForm = ({ article, categories, onChange, onError, onSuccess }) => {
  const [cropFile, setCropFile] = useState(null);

  const handleChange = (field, value) => onChange({ ...article, [field]: value });

  const handleMainImageUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setCropFile(file);
  };

  const handleCropCancel = () => {
    setCropFile(null);
  };

  const handleCropConfirm = async (croppedFile) => {
    try {
      const result = await uploadContentImage(croppedFile, IMAGE_SCOPES.ARTICLES);
      handleChange("image", result.publicUrl);
      onSuccess("Immagine principale caricata.");
      setCropFile(null);
    } catch (error) {
      onError(error.message);
    }
  };

  return (
    <>
      <Card sx={contentEditorCardSx}>
      <CardContent sx={contentCardContentSx}>
        <Typography sx={contentSectionTitleSx}>Dati articolo</Typography>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <TextField
              size="small"
              label="Titolo"
              value={article.title}
              onChange={(event) => handleChange("title", event.target.value)}
              required
              fullWidth
              sx={{ ...contentFieldSx, flex: 5 }}
            />
            <FormControl
              sx={{
                flex: 1,
                minWidth: 120,
                border: `1px dashed ${article.isVisible ? "#008245" : "#ad0029"}`,
                borderRadius: 2,
                height: 39,
                display: "flex",
                justifyContent: "center",
                alignItems: "left",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={article.isVisible}
                    onChange={(event) => handleChange("isVisible", event.target.checked)}
                  />
                }
                label={article.isVisible ? "Articolo visibile" : "Articolo non visibile"}
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.9rem", whiteSpace: "nowrap" }
                }}
              />
            </FormControl>
          </Stack>
  
          <TextField
            size="small"
            label="Sottotitolo"
            value={article.subtitle}
            onChange={(event) => handleChange("subtitle", event.target.value)}
            multiline
            minRows={2}
            fullWidth
            sx={contentFieldSx}
          />
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
            <FormControl fullWidth required size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                label="Categoria"
                value={article.category}
                onChange={(event) => handleChange("category", event.target.value)}
              >
                {categories.map((category) => (
                  <MenuItem key={category.name} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Data pubblicazione"
              type="datetime-local"
              value={article.publishedAt}
              onChange={(event) => handleChange("publishedAt", event.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              sx={contentFieldSx}
            />
            <TextField
              size="small"
              label="Minuti lettura"
              type="number"
              value={article.readingMinutes}
              onChange={(event) => handleChange("readingMinutes", event.target.value)}
              slotProps={{ htmlInput: { min: 1 } }}
              fullWidth
              sx={contentFieldSx}
            />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "center" }}>
            <Button size="small" component="label" variant="outlined" startIcon={<CloudUploadIcon />} sx={{ width: 350, height: 39, }}>
              Carica immagine principale
              <input hidden type="file" accept="image/*" onChange={handleMainImageUpload} />
            </Button>
            <TextField
              size="small"
              label="URL immagine principale"
              value={article.image}
              onChange={(event) => handleChange("image", event.target.value)}
              required
              fullWidth
              sx={contentFieldSx}
            />
          </Stack>
          {article.image ? (
            <Box
              component="img"
              src={article.image}
              alt="Anteprima articolo"
              sx={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 1.5, border: "1px solid #e8edf3" }}
            />
          ) : null}

        </Stack>
      </CardContent>
    </Card>
      <ArticleImageCropDialog
        open={Boolean(cropFile)}
        imageFile={cropFile}
        aspectRatio={ARTICLE_HERO_IMAGE_ASPECT}
        title="Ritaglia immagine principale"
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
      />
    </>
  );
};

ArticleMetadataForm.propTypes = {
  article: PropTypes.object.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string.isRequired })).isRequired,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ArticleMetadataForm;
